import { Link, useNavigate } from '@tanstack/react-router'
import {
  ChevronLeft,
  LayoutGrid,
  MoreVertical,
  Pencil,
  Plus,
  Printer,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useGeneratePosPage,
  usePrintLink,
  useUpdateMenu,
} from '../hooks/use-menu-mutations'
import { menuDisplayName } from '../lib/menu-display'
import { PublishMenuButton } from './publish-menu-button'
import { UnpublishMenuButton } from './unpublish-menu-button'
import { DeleteMenuDialog } from './delete-menu-dialog'
import type { Menu } from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface MenuEditorHeaderProps {
  menu: Menu
  onAddSection: () => void
}

export function MenuEditorHeader({
  menu,
  onAddSection,
}: MenuEditorHeaderProps) {
  const { t: tMenus } = useTranslation('menus')
  const navigate = useNavigate()
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const { can } = usePermissions()
  // Master menus are never deletable, matching MenuDeleteMenu's own guard.
  const canDelete = menu.type !== 'master' && can(ACTIONS.MENU_DELETE)
  const [nameDraft, setNameDraft] = useState(menu.name)

  const isDaily = menu.type === 'daily'
  const updateMenu = useUpdateMenu()
  const generatePosPage = useGeneratePosPage()
  const printLink = usePrintLink()

  /**
   * The Karte itself lives in the order app, so the preview means opening it there with a token
   * that unlocks this day. The tab is opened synchronously on the click — opening it only after
   * the request returns is what popup blockers stop.
   *
   * No `noopener` in the feature string: with it, `window.open` returns null, and there is then
   * no handle to point at the minted URL. The opener link is severed on the handle instead, which
   * has the same effect without losing the reference.
   *
   * The backoffice never navigates itself away. If the tab could not be opened at all the editor
   * stays put and says so, rather than replacing the page the user is working in.
   */
  const handlePreview = () => {
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    printLink.mutate(menu.id, {
      onSuccess: (result) => {
        if (!tab) {
          toast.error(tMenus('editor.menuPreviewBlocked'))
          return
        }
        // replace, not href: Zurück in that tab should leave the preview, not land on about:blank.
        tab.location.replace(result.url)
        tab.focus()
      },
      onError: () => tab?.close(),
    })
  }

  const title = isDaily ? menuDisplayName(menu) : tMenus('editor.masterTitle')

  const handleRename = () => {
    const trimmed = nameDraft.trim()
    if (trimmed && trimmed !== menu.name) {
      updateMenu.mutate({ menuId: menu.id, data: { name: trimmed } })
    }
    setRenameOpen(false)
  }

  return (
    <>
      <div className="border-b bg-background">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/menus"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-4 h-4" />
              {tMenus('editor.back')}
            </Link>

            <div className="h-6 w-px bg-border" />

            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold">{title}</h1>
                <Badge variant="outline">{menu.type}</Badge>
                {isDaily && (
                  <Badge
                    variant={
                      menu.status === 'published' ? 'default' : 'secondary'
                    }
                  >
                    {menu.status}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* One emphasised action — publish/unpublish is what the screen exists for. Everything
              occasional lives in the overflow menu (CHG-2026-09-menu-editor-redesign). */}
          <div className="flex items-center gap-2">
            {isDaily && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
                disabled={printLink.isPending}
              >
                <Printer className="w-4 h-4 mr-2" />
                {printLink.isPending
                  ? tMenus('editor.menuPreviewPending')
                  : tMenus('editor.menuPreview')}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={onAddSection}>
              <Plus className="w-4 h-4 mr-2" />
              {tMenus('editor.addSection')}
            </Button>

            {isDaily && (
              <>
                <div className="mx-1 h-5 w-px bg-border" />
                {menu.status === 'draft' ? (
                  <PublishMenuButton menu={menu} />
                ) : (
                  <UnpublishMenuButton menu={menu} />
                )}
              </>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setNameDraft(menu.name)
                    setRenameOpen(true)
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {tMenus('editor.rename')}
                </DropdownMenuItem>
                {isDaily && (
                  <DropdownMenuItem
                    onClick={() =>
                      generatePosPage.mutate({
                        restaurantId: menu.restaurantId,
                        menuId: menu.id,
                      })
                    }
                    disabled={generatePosPage.isPending}
                  >
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    {tMenus('editor.generatePosPage')}
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteOpen(true)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {tMenus('list.actions.delete')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <DeleteMenuDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        menu={menu}
        onDeleted={() => navigate({ to: '/menus' })}
      />

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tMenus('editor.renameTitle')}</DialogTitle>
            <DialogDescription>
              {tMenus('editor.renameDescription')}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename()
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              {tMenus('editor.renameCancel')}
            </Button>
            <Button onClick={handleRename} disabled={updateMenu.isPending}>
              {tMenus('editor.renameSave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
